import { Navigate, useParams } from 'react-router-dom';
import { useMemo } from 'react';
import KeepLink from '../components/KeepLink';
import { useGraphIndex } from '../hooks/useGraphIndex';
import { useLang } from '../hooks/useLang';
import { adhigaramSlugToN } from '../data/adhigaramSlug';
// Reuse the Theme view stylesheet — same layout primitives (title
// eyebrow, bilingual head, kural list). No new CSS.
import './Theme.css';

export default function Adhigaram() {
    const { slug } = useParams<{ slug: string }>();
    const graph = useGraphIndex();
    const [lang] = useLang();

    const view = useMemo(() => {
        if (!graph.data || !slug) return null;
        const slugToN = adhigaramSlugToN(graph.data.adhigarams);
        const n = slugToN.get(slug);
        if (n === undefined) return { adhigaram: null };
        const adhigaram = graph.data.adhigarams.find((a) => a.n === n);
        if (!adhigaram) return { adhigaram: null };
        const kuralByN = new Map(graph.data.kurals.map((k) => [k.n, k]));
        const kurals = adhigaram.kural_ns
            .map((kn) => kuralByN.get(kn))
            .filter((k): k is NonNullable<typeof k> => k !== undefined);
        const paal = graph.data.paals.find((p) => p.slug === adhigaram.paal);
        return { adhigaram, kurals, paal };
    }, [graph.data, slug]);

    if (graph.error) {
        return (
            <div className="focus-error" role="alert">
                <p>Sorry — we couldn't load the corpus index. Refresh to try again.</p>
            </div>
        );
    }
    if (graph.loading || !view) {
        return (
            <div className="focus-loading" aria-live="polite">
                <span className="focus-loading__dot" aria-hidden="true" />
                <span>Loading adhigaram…</span>
            </div>
        );
    }
    if (view.adhigaram === null) {
        return <Navigate to="/adhigarams" replace />;
    }
    const { adhigaram, kurals, paal } = view;

    return (
        <div className="theme-view">
            <header className="theme-view__head">
                <p className="theme-view__eyebrow">Adhigaram {adhigaram.n}</p>
                <h1 className="theme-view__title">
                    {lang === 'ta' ? (
                        <>
                            <span lang="ta" className="theme-view__title-lead">
                                {adhigaram.title_ta}
                            </span>
                            <span className="theme-view__title-sub">
                                {adhigaram.title_en}
                            </span>
                        </>
                    ) : (
                        <>
                            <span className="theme-view__title-lead">
                                {adhigaram.title_en}
                            </span>
                            <span lang="ta" className="theme-view__title-sub">
                                {adhigaram.title_ta}
                            </span>
                        </>
                    )}
                </h1>
                <p className="theme-view__desc">
                    {lang === 'ta' ? (
                        <>
                            <span lang="ta">{paal?.title_ta ?? adhigaram.paal}</span>
                            {' · '}
                            <span lang="ta">{adhigaram.iyal}</span>
                        </>
                    ) : (
                        <>
                            {paal?.title_en ?? adhigaram.paal}
                            {' · '}
                            {adhigaram.iyal_en}
                        </>
                    )}
                </p>
                <p className="theme-view__count">
                    {kurals!.length} {kurals!.length === 1 ? 'kural' : 'kurals'}.
                </p>
            </header>

            <section className="theme-view__list" aria-label="Kurals in this adhigaram">
                <h2 className="theme-view__section-title">Kurals</h2>
                <div className="theme-view__group">
                    <ul className="theme-view__kurals">
                        {kurals!.map((k) => (
                            <li key={k.n}>
                                <KeepLink
                                    to={`/kural/${k.n}`}
                                    className="theme-view__kural"
                                    aria-label={`Open kural ${k.n}: ${k.first_line_ta}. ${k.gist_en}`}
                                >
                                    <span className="theme-view__kural-n">Kural {k.n}</span>
                                    <span lang="ta" className="theme-view__kural-ta">
                                        {(k.verse_ta && k.verse_ta.length > 0
                                            ? k.verse_ta
                                            : [k.first_line_ta]
                                        ).map((line, i) => (
                                            <span
                                                key={i}
                                                className="theme-view__kural-ta-line"
                                            >
                                                {line}
                                            </span>
                                        ))}
                                    </span>
                                    <span className="theme-view__kural-gist">{k.gist_en}</span>
                                </KeepLink>
                            </li>
                        ))}
                    </ul>
                </div>
            </section>
        </div>
    );
}
